import { apiClient } from './client.js'

// Helper function to build query parameters
function buildQueryParams(params = {}) {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value)
    }
  })
  const query = searchParams.toString()
  return query ? `?${query}` : ''
}

// ==================== CONVERSION RULES ====================

/**
 * Create a new unit conversion rule for a product
 * @param {{
 *   product_id: string,
 *   variant_id?: string,
 *   from_unit_id: string,
 *   to_unit_id: string,
 *   conversion_factor: number,
 *   notes?: string
 * }} data
 */
export const createConversionRule = async (data) => {
  return apiClient.post('/inventory/conversions/rules', data)
}

/**
 * Get all conversion rules for a product
 * @param {Object} params - Query parameters
 * @param {string} params.product_id - Product UUID (required)
 * @param {string} params.variant_id - Variant UUID (optional)
 */
export const getConversionRules = async (params = {}) => {
  const query = buildQueryParams(params)
  return apiClient.get(`/inventory/conversions/rules${query}`)
}

/**
 * Get a specific conversion rule by ID
 * @param {string} id - Conversion rule UUID
 */
export const getConversionRule = async (id) => {
  return apiClient.get(`/inventory/conversions/rules/${id}`)
}

/**
 * Update an existing conversion rule
 * @param {string} id - Conversion rule UUID
 * @param {{
 *   conversion_factor?: number,
 *   is_active?: boolean,
 *   notes?: string
 * }} data
 */
export const updateConversionRule = async (id, data) => {
  return apiClient.put(`/inventory/conversions/rules/${id}`, data)
}

/**
 * Delete a conversion rule (soft delete)
 * @param {string} id - Conversion rule UUID
 */
export const deleteConversionRule = async (id) => {
  return apiClient.delete(`/inventory/conversions/rules/${id}`)
}

/**
 * Find a conversion rule by its unit barcode (untuk scan di kasir)
 * @param {string} barcode - Barcode satuan jual (mis. barcode dus/lusin)
 */
export const scanConversionRuleByBarcode = async (barcode) => {
  const query = buildQueryParams({ barcode })
  return apiClient.get(`/inventory/conversions/rules/scan${query}`)
}

// ==================== STOCK CONVERSIONS ====================

/**
 * Execute a stock unit conversion
 * @param {{
 *   product_id: string,
 *   variant_id?: string,
 *   from_unit_id: string,
 *   to_unit_id: string,
 *   qty: number,
 *   notes?: string
 * }} data
 */
export const convertStock = async (data) => {
  return apiClient.post('/inventory/conversions', data)
}

/**
 * Get list of stock conversions with filters
 * @param {Object} params - Query parameters
 * @param {string} params.product_id - Filter by product
 * @param {string} params.variant_id - Filter by variant
 * @param {string} params.status - Filter by status (DRAFT, COMPLETED, CANCELLED)
 * @param {string} params.start_date - Filter from date (YYYY-MM-DD)
 * @param {string} params.end_date - Filter to date (YYYY-MM-DD)
 * @param {string} params.q - Search by conversion number
 * @param {number} params.limit - Items per page (default: 20)
 * @param {number} params.offset - Pagination offset (default: 0)
 */
export const getConversions = async (params = {}) => {
  const query = buildQueryParams(params)
  return apiClient.get(`/inventory/conversions${query}`)
}

/**
 * Get detailed information about a specific conversion
 * @param {string} id - Conversion UUID
 */
export const getConversionDetail = async (id) => {
  return apiClient.get(`/inventory/conversions/${id}`)
}

// ==================== EXPORT ALL SERVICES ====================

export const conversionService = {
  // Conversion Rules
  rules: {
    create: createConversionRule,
    list: getConversionRules,
    get: getConversionRule,
    update: updateConversionRule,
    delete: deleteConversionRule,
    scanBarcode: scanConversionRuleByBarcode
  },
  
  // Stock Conversions
  convert: convertStock,
  list: getConversions,
  get: getConversionDetail
}

export default conversionService
